/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Box, Text, Spacer } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { BusinessDetails } from "~/components/BasicCustomerInfo/BasicInfoComponents/BusinessDetails";
import { CustomerDetails } from "~/components/BasicCustomerInfo/BasicInfoComponents/CustomerDetails";
import { DedicatedEmails } from "~/components/BasicCustomerInfo/BasicInfoComponents/DedicatedEmails";
import { NamedLinks } from "~/components/BasicCustomerInfo/BasicInfoComponents/NamedLinks";
import { HistoryOfEditing } from "~/components/BasicCustomerInfo/HistoryOfEditing";
import {
  BasicInfoEditModeInitialState,
  BasicInfoLoadingInitialState,
} from "~/components/BasicCustomerInfo/initialState";
import {
  BasicInfoEditModeType,
  BasicInfoLoadingType,
  Customer,
} from "~/components/BasicCustomerInfo/types";
import { DeleteCustomer } from "~/components/Customer/DeleteCustomer";
import { useShowError } from "~/components/Error/useShowError";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { Divider } from "~/components/Styled/Divider";
import { showSuccess } from "~/components/Toast";
import { CustomerInitialState, useCustomer } from "~/context/User/Customer";
import { useUserConsumer } from "~/context/User/User";
import { ACTIONS, Can, isStandardUser } from "~/routing";
import { useAxios, generateActionSuccessText } from "~/tools";

function BasicInfo() {
  const {
    customer: customerInitialState,
    fetchCustomer: refetchCustomer,
    isCustomerLoading,
  } = useCustomer();

  const [isLoading, setIsLoading] = React.useState<BasicInfoLoadingType>(
    BasicInfoLoadingInitialState
  );

  const [isEditMode, setIsEditMode] = React.useState<BasicInfoEditModeType>(
    BasicInfoEditModeInitialState
  );

  const [customer, setCustomer] = React.useState<Customer>(
    CustomerInitialState
  );

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const { showError } = useShowError();
  const { Axios } = useAxios();
  const onSubmit = async (values: Customer) => {
    try {
      await Axios().put(`/customers/${customer.id}`, values);
      setIsLoading(BasicInfoLoadingInitialState);
      setIsEditMode(BasicInfoEditModeInitialState);
      showSuccess(
        generateActionSuccessText(`Customer ${customer.name}`)()("updated")()
      );
    } catch (e) {
      setIsLoading(BasicInfoLoadingInitialState);
      setIsEditMode(BasicInfoEditModeInitialState);
      showError(e.response);
    }

    return refetchCustomer();
  };

  React.useEffect(() => {
    !isCustomerLoading && setCustomer(customerInitialState);
  }, [customerInitialState, isCustomerLoading]);

  return (
    <LoaderWrap loading={isCustomerLoading}>
      <Box spacing="spacing04">
        <Divider>
          <FlexContainer align="center">
            <Text isTitle weight="medium" css={{ minWidth: "12rem" }}>
              Customer Details
            </Text>
            <HistoryOfEditing customer={customer} />
          </FlexContainer>
          <CustomerDetails
            item={customer}
            onSubmit={onSubmit}
            canEdit={canEdit}
          />
          <Spacer height="1.5rem" />
        </Divider>
        <Divider>
          <Spacer height="1rem" />
          <Text isTitle weight="medium">
            Business Details
          </Text>
          <BusinessDetails
            item={customer}
            onSubmit={onSubmit}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            canEdit={canEdit}
          />
          <Spacer height="1.5rem" />
        </Divider>
        <Spacer height="1rem" />
        <NamedLinks
          name="Links"
          items={customer?.info?.links}
          customer={customer}
          onSubmit={onSubmit}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          data-cy="Links"
          canEdit={canEdit}
        />
        <Spacer height="1rem" />

        <Text isTitle weight="medium">
          Customer Dedicated Emails
        </Text>
        <DedicatedEmails
          customer={customer}
          onSubmit={onSubmit}
          canEdit={canEdit}
        />
      </Box>

      <Can action={ACTIONS.DELETE_CUSTOMER}>
        <DeleteCustomer customer={customer} />
      </Can>
    </LoaderWrap>
  );
}

export { BasicInfo };
